import 'css/src/components/share-video/SpinoffCard';

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
} from 'js/src/components/common/ui/Button';
import { CanvasPreview } from 'js/src/components/common/ui/CanvasPreview';
import { ContentPlaceholderBlock } from 'js/src/components/common/ui/ContentPlaceholder';
import { selectLuminaryThumbnail } from 'js/src/data/thumbnail-previews';
import { getVideoPlanById, loadVideoPlanOperation } from 'js/src/data/video-planner';
import { NARRATIVE_THREAD_DISPLAY_STRING_MAP, NARRATIVE_THREAD_ICON_MAP } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { getTimeStringFromSeconds } from 'js/src/libs/video-duration-tools';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

export const SpinoffCard = ({
    videoPlanId,
    onShowPreview,
    isSaved,
}: {
    videoPlanId: string;
    onShowPreview: (planId: string) => void;
    isSaved: boolean;
}) => {
    const { loadVideoPlan } = useLuminaryOperations({
        loadVideoPlanOperation,
    });

    const videoPlan = useSelector((state: RootState) => getVideoPlanById(state, videoPlanId));
    if (!videoPlan) {
        throw new Error('Invalid video plan');
    }
    const hasFullyLoadedRecommendations = videoPlan.scene_recommendations?.every(
        (scene) => scene !== null,
    );

    const previewCanvas = useSelector((state: RootState) =>
        selectLuminaryThumbnail(state, videoPlan.id),
    );

    useEffect(() => {
        const initLuminary = async () => {
            await loadVideoPlan(videoPlan, false);
        };

        if (videoPlan && hasFullyLoadedRecommendations) {
            void initLuminary();
        }
    }, [hasFullyLoadedRecommendations, loadVideoPlan, videoPlan]);

    const showPreviewModal = () => {
        analytics.track('Spinoff Previewed');
        if (isSaved) {
            return;
        }
        onShowPreview(videoPlan.id);
    };

    return (
        <>
            <div
                className={classNames('position-relative spinoff-card w-100', {
                    'spinoff-card--disabled': isSaved,
                    'cursor-pointer': !isSaved,
                })}
                onClick={showPreviewModal}
            >
                <div className="spinoff-card__content w-100 p-l5-8 d-flex gap-2">
                    <div className="spinoff-card__preview position-relative">
                        {previewCanvas ? (
                            <div
                                className={`spinoff-card__preview__canvas position-relative h-100 ${videoPlan.aspect_ratio}`}
                            >
                                <CanvasPreview
                                    className="h-100 w-100 position-absolute"
                                    sourceCanvas={previewCanvas}
                                />
                            </div>
                        ) : (
                            <ContentPlaceholderBlock />
                        )}
                        {!isSaved && (
                            <div className="spinoff-card__preview__button position-absolute top-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                <Button
                                    color={BUTTON_COLORS.PRIMARY}
                                    size={BUTTON_SIZES.SM}
                                    text="Preview"
                                    variant={BUTTON_VARIANTS.OUTLINE}
                                />
                            </div>
                        )}
                    </div>
                    <div className="spinoff-card__meta d-flex flex-column justify-content-between overflow-hidden">
                        <div className="spinoff-card__meta__title font-l5 font-l5--subtitle-l--semibold color-grey-900">
                            {videoPlan.title}
                        </div>
                        <div className="spinoff-card__meta__info font-l5 font-l5--subtext color-grey-600">
                            {videoPlan.narrative_thread && (
                                <>
                                    <i className="material-symbols-outlined icon-l5 icon-l5--medium me-l5-2">
                                        {NARRATIVE_THREAD_ICON_MAP[videoPlan.narrative_thread]}
                                    </i>
                                    <span className="align-middle me-l5-12">
                                        {
                                            NARRATIVE_THREAD_DISPLAY_STRING_MAP[
                                                videoPlan.narrative_thread
                                            ]
                                        }
                                    </span>
                                </>
                            )}
                            {videoPlan.computed_duration_ms && (
                                <>
                                    <i className="material-symbols-outlined icon-l5 icon-l5--medium me-l5-2">
                                        timer
                                    </i>
                                    <span className="align-middle">
                                        {getTimeStringFromSeconds(
                                            videoPlan.computed_duration_ms / 1000,
                                        )}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isSaved && (
                    <div className="top-0 left-0 position-absolute w-100 h-100 spinoff-card__overlay d-flex align-items-end justify-content-end p-l5-8">
                        <div className="d-flex align-items-center justify-content-end overlay-group font-l5 font-l5--subtitle-l color-grey-800">
                            <i className="material-symbols-outlined icon-l5 icon-l5--medium me-l5-6">
                                check
                            </i>
                            Saved
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
